// `join` pipeline verb — combine the threaded dataset with another
// `Dataset` value on a set of key columns.
//
// Surface:
//
//   (join other-ds :on [well-id] :type left)
//   (join other-ds :on [[outer-key inner-key]] :type inner)
//   (join other-ds :on [(col location id)] :type inner)
//   (join other-ds :on [[(col loc id) (col ref)]] :type left)
//
// Special-form: arg[0] is a Dataset value (typically a binding name
// that resolves through Env → Session via the engine's symbol-ref
// fallthrough); `:on` is a vector of key specs.
//
// `:on` resolution is ROLE-BASED. The OUTER side of every key always
// resolves against the THREADED dataset; the INNER side always
// against the OTHER dataset. A side is one of:
//
//   - a bare symbol            → that (non-struct) column on the
//                                 role dataset
//   - `(col <column> <sub>…)`  → a struct sub-path key on the role
//                                 dataset
//
// A `(col …)` form may carry a leading dataset/binding name for
// readability (`(col wells location id)`); it is IGNORED — the role
// (outer vs inner), not the name, decides which dataset the key is
// read from. The first label that names a column on the role dataset
// is the column; any labels before it are treated as a (skipped)
// binding name and the labels after it as the struct sub-path.
//
// Two single-side keys (`:on [k1 k2]`) that cannot both be self-join
// keys are auto-interpreted as a single cross pair
// `[[k1 k2]]` (k1 against the threaded dataset, k2 against the
// other) — the common "join my key to their key" intent. `:type`
// (inner | left | right | full) is orthogonal and never changed by
// this; it defaults to `inner`.
//
// Output schema: outer columns first, then inner columns. Non-key
// columns whose names collide are suffixed (`name_1` / `name_2`) by
// the runtime join rather than rejected. Synthetic key columns added
// for `(col …)` sub-path sides are dropped from the output.

import { z } from 'zod';

import { pickColumnPath, resolveBindingDataset, walkStructPath } from '../../../column-ref.ts';
import { ShapeError, TypeMismatchError } from '../../../errors.ts';
import { type AstInput, astShape, opCallPos } from '../../../normalize.ts';
import { type ColumnRefInArg, pipelineVerbs, type SpecialVerbCtx } from '../../../op-meta.ts';
import { paramPath } from '../../../path.ts';
import { type JoinType, runtimeJoin } from '../../../runtime/arquero.ts';
import { type ColumnType, schemaFromEntries, unknownCol, unwrapNullable } from '../../../schema/types.ts';
import { type Cell, type Dataset, expectDataset, expectMaterialized, MaterializedDataset } from '../../../value.ts';

// Mirror the `JoinType` enum from `runtime/arquero.ts` as a const
// tuple so Zod's `z.enum` can validate it. Keeping the values in
// lockstep with `runtime/arquero.ts`'s `JoinType` is verified by the
// `satisfies` check below — adding a variant to one without the
// other surfaces a compile-time error.
const JoinTypes = ['inner', 'left', 'right', 'full'] as const satisfies readonly JoinType[];

// A single `:on` side: a bare symbol (matched column name) OR a raw
// `(col …)` opCall AST node (struct sub-path key). The `(col …)`
// shape is validated structurally at apply time (same pattern as
// other special-form params that carry raw AST) rather than via a
// precise Zod schema, so we accept any opCall here and reject a
// malformed one with a targeted `ShapeError`.
const OnSide = z.union([z.object({ $sym: z.string().min(1) }), z.object({ name: z.literal('col') }).loose()]);

// `:on` entry — either a single side (self / cross-coerced) or a
// two-side tuple (explicit `[outer inner]` pair, each side
// independently a symbol or a `(col …)`).
const OnEntry = z.union([OnSide, z.tuple([OnSide, OnSide])]);

type Pos = ReturnType<typeof opCallPos>;

const isSym = (v: unknown): v is { $sym: string } =>
  v !== null && typeof v === 'object' && '$sym' in (v as { $sym?: unknown });

const isColNode = (v: unknown): v is { params?: readonly AstInput[] } =>
  v !== null && typeof v === 'object' && (v as { name?: unknown }).name === 'col';

/**
 * Strict label extractor for a `:on` side. A side's labels are the
 * bare symbol (length 1) or the `(col …)` arg list. Every arg must
 * be a symbol or string literal — a `(col (expr) …)` Form-2 first
 * arg is unsupported inside `:on` and surfaces a targeted
 * `ShapeError` rather than an opaque failure. Throws on a malformed
 * side; used on the apply path.
 */
const sideLabels = (side: unknown, role: string, pos: Pos): readonly string[] => {
  if (isSym(side)) {
    return [side.$sym];
  }
  if (isColNode(side)) {
    const params = (side.params ?? []) as readonly AstInput[];
    if (params.length === 0) {
      throw new ShapeError(`:on ${role} \`(col …)\` needs at least a column label`, pos);
    }
    return params.map((p) => {
      const s = astShape(p);
      if (s.kind === 'symbolRef') {
        return s.sym;
      }
      if (s.kind === 'primitive' && typeof s.value === 'string') {
        return s.value;
      }
      throw new ShapeError(
        `:on ${role} \`(col …)\` labels must be symbols or string literals (Form-2 \`(col (expr) …)\` is not supported inside join :on)`,
        pos,
      );
    });
  }
  throw new ShapeError(
    `:on ${role} must be a bare column symbol or a \`(col [<binding>] <column> [<subfield>…])\` reference`,
    pos,
  );
};

/**
 * Tolerant label extractor (no throw) — `undefined` when the side is
 * malformed or carries a non-literal arg. Used by `referencedColumns`
 * (type-check time, must not throw) and the flat→cross coercion
 * heuristic.
 */
const sideLabelsLoose = (side: unknown): readonly string[] | undefined => {
  if (isSym(side)) {
    return [side.$sym];
  }
  if (isColNode(side)) {
    const params = (side.params ?? []) as readonly AstInput[];
    const out: string[] = [];
    for (const p of params) {
      const s = astShape(p);
      if (s.kind === 'symbolRef') {
        out.push(s.sym);
      } else if (s.kind === 'primitive' && typeof s.value === 'string') {
        out.push(s.value);
      } else {
        return undefined;
      }
    }
    return out;
  }
  return undefined;
};

/**
 * Validate a struct sub-path against the column's type. An
 * `unknown`-typed column (every `transform` / `aggregate` / `join`
 * output) can't be proven invalid statically, so the walk is allowed
 * and the runtime path-walk handles a genuine mismatch (null).
 */
const structField = (t: ColumnType, column: string, seg: string, which: string, pos: Pos): ColumnType => {
  if (t.kind !== 'struct') {
    throw new TypeMismatchError(
      `:on ${which} key \`${column}\`: sub-path segment \`${seg}\` — \`${column}\` is not a struct`,
      'struct sub-path on a non-struct column',
      'join',
      pos,
    );
  }
  const next = t.fields[seg];
  if (next === undefined) {
    throw new TypeMismatchError(
      `:on ${which} key \`${column}\` has no sub-field \`${seg}\` (available: ${Object.keys(t.fields).join(', ') || '—'})`,
      'no such struct sub-field',
      'join',
      pos,
    );
  }
  return unwrapNullable(next);
};

const validatePath = (colType: ColumnType, column: string, path: readonly string[], which: string, pos: Pos): void => {
  let t = unwrapNullable(colType);
  for (const seg of path) {
    if (t.kind === 'unknown') {
      return;
    }
    t = structField(t, column, seg, which, pos);
  }
};

export const join = z
  .object({
    name: z.literal('join'),
    params: z.tuple([z.unknown()]), // first positional = other Dataset (raw AST, evaluated by apply)
    on: z.array(OnEntry).min(1),
    type: z.enum(JoinTypes).default('inner'),
  })
  .describe(
    'Join the threaded dataset with another Dataset on key columns. Positional 0: the other Dataset (typically a binding name). `:on` is a vector of keys; resolution is role-based — the OUTER side resolves against the threaded dataset, the INNER side against the other dataset. A side is a bare column symbol or a `(col [<binding>] <column> [<subfield>…])` struct sub-path; any leading binding name in `(col …)` is ignored (role decides the dataset). Forms: `[k]` (same column both sides), `[[outer inner]]` (explicit cross pair), `[ko ki]` (two single keys → auto-treated as one cross pair when not both self-keys). `:type` is inner | left | right | full (orthogonal to `:on`).',
  )
  .register(pipelineVerbs, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(transform ds (join other :on [[(col wellString id) (col string id)]] :type left))' },
    apply: async (data, dataset: MaterializedDataset, ctx: SpecialVerbCtx): Promise<MaterializedDataset> => {
      const callPos = opCallPos(ctx.spans, ctx.path);
      // Evaluate positional[0] as an AST node — produces a Dataset
      // (via session binding lookup or inline Dataset literal). We
      // need to materialise it before arquero crosses the boundary.
      const otherRaw = (data.params as readonly unknown[])[0];
      const other = await resolveDataset(otherRaw, ctx, paramPath(ctx.path, 0));
      const otherMat = expectMaterialized(other, '(join <other-Dataset> …)', callPos);

      let outerCols: Record<string, readonly Cell[]> = { ...dataset.columns };
      let outerSchema = schemaFromEntries([...dataset.schema]);
      let innerCols: Record<string, readonly Cell[]> = { ...otherMat.columns };
      let innerSchema = schemaFromEntries([...otherMat.schema]);
      const syntheticKeys: string[] = [];

      // Flat → cross coercion: two single-side keys that cannot both
      // be self-join keys (same column present on BOTH datasets) but
      // do line up as `outer-key` (threaded) + `inner-key` (other)
      // are the "join my key to their key" intent — reinterpret as a
      // single `[[k1 k2]]` pair. `:type` is untouched.
      const onBoth = (labels: readonly string[]): boolean =>
        pickColumnPath(labels, (n) => dataset.schema.has(n)) !== undefined &&
        pickColumnPath(labels, (n) => otherMat.schema.has(n)) !== undefined;
      const coerceFlatOn = (on: readonly unknown[]): readonly unknown[] => {
        if (on.length !== 2 || Array.isArray(on[0]) || Array.isArray(on[1])) {
          return on;
        }
        const l0 = sideLabelsLoose(on[0]);
        const l1 = sideLabelsLoose(on[1]);
        if (l0 === undefined || l1 === undefined) {
          return on;
        }
        const crossResolves =
          pickColumnPath(l0, (n) => dataset.schema.has(n)) !== undefined &&
          pickColumnPath(l1, (n) => otherMat.schema.has(n)) !== undefined;
        if (!(onBoth(l0) && onBoth(l1)) && crossResolves) {
          return [[on[0], on[1]]];
        }
        return on;
      };

      const entrySides = (entry: unknown): { readonly outer: unknown; readonly inner: unknown } =>
        Array.isArray(entry) ? { outer: entry[0], inner: entry[1] } : { outer: entry, inner: entry };

      const resolveSide = (side: unknown, which: 'outer' | 'inner', idx: number): string => {
        const schema = which === 'outer' ? dataset.schema : otherMat.schema;
        const cols = which === 'outer' ? dataset.columns : otherMat.columns;
        const dsLabel = which === 'outer' ? 'threaded' : 'other';
        const labels = sideLabels(side, which === 'outer' ? 'outer side' : 'inner side', callPos);
        const picked = pickColumnPath(labels, (n) => schema.has(n));
        if (picked === undefined) {
          throw new TypeMismatchError(
            `:on ${which} key — none of [${labels.join(' ')}] is a column on the ${dsLabel} dataset (available: ${[...schema.keys()].join(', ') || '—'})`,
            'no matching column on the join side',
            'join',
            callPos,
          );
        }
        const { column, path } = picked;
        const colType = schema.get(column);
        if (path.length === 0) {
          if (colType !== undefined && unwrapNullable(colType).kind === 'struct') {
            throw new TypeMismatchError(
              `:on ${which} column \`${column}\` is a struct; join on a sub-path with \`(col ${column} <subfield>)\``,
              'struct key needs a sub-path',
              'join',
              callPos,
            );
          }
          return column; // plain-name key — no synthetic column needed
        }
        // Struct sub-path key → validate, then project to a synthetic
        // scalar key column on the role dataset. Collision-safe
        // synthetic names (`__joinkey_l_<i>` / `__joinkey_r_<i>`);
        // dropped from the output after the join.
        if (colType !== undefined) {
          validatePath(colType, column, path, which, callPos);
        }
        const baseCells = cols[column] ?? [];
        const walked = baseCells.map((cell) => walkStructPath(cell, path)) as readonly Cell[];
        const synth = which === 'outer' ? `__joinkey_l_${idx}` : `__joinkey_r_${idx}`;
        syntheticKeys.push(synth);
        if (which === 'outer') {
          outerCols = { ...outerCols, [synth]: walked };
          outerSchema = schemaFromEntries([...outerSchema, [synth, unknownCol]]);
        } else {
          innerCols = { ...innerCols, [synth]: walked };
          innerSchema = schemaFromEntries([...innerSchema, [synth, unknownCol]]);
        }
        return synth;
      };

      const rawOn = coerceFlatOn(data.on as readonly unknown[]);
      const keyPairs = rawOn.map((entry, i) => {
        const { outer, inner } = entrySides(entry);
        return { left: resolveSide(outer, 'outer', i), right: resolveSide(inner, 'inner', i) };
      });

      const joinType: JoinType = data.type;

      // Hand off to the §8.1 boundary helper with the (possibly
      // synthetic-key-augmented) materialised tables. Non-key column
      // name collisions are suffixed by the runtime join (`name_1` /
      // `name_2`) — the LLM can `rename` afterwards. The orderby is
      // preserved per arquero's left-input bias for inner / left /
      // full joins; the engine doesn't surface that ordering as part
      // of the join contract.
      const leftDs = new MaterializedDataset(outerCols, outerSchema);
      const rightDs = new MaterializedDataset(innerCols, innerSchema);
      const joined = runtimeJoin(
        leftDs,
        rightDs,
        [keyPairs.map((p) => p.left), keyPairs.map((p) => p.right)],
        joinType,
      );

      // Drop the synthetic key columns from the output schema +
      // columns — they were a join-internal device, not part of the
      // user-visible result.
      if (syntheticKeys.length === 0) {
        return joined;
      }
      const drop = new Set(syntheticKeys);
      const keptCols = Object.fromEntries(Object.entries(joined.columns).filter(([k]) => !drop.has(k)));
      const keptSchema = schemaFromEntries([...joined.schema].filter(([k]) => !drop.has(k)));
      return new MaterializedDataset(keptCols, keptSchema);
    },
    // No `propagateSchema` — `PropagateSchemaCtx` doesn't carry the
    // session or env, so we can't see the OTHER dataset's schema at
    // type-check time. Letting the runtime-computed schema (from
    // `fromTable` in `runtimeJoin`) flow through is the pragmatic
    // choice: apply produces a `MaterializedDataset` with the full
    // joined columns and `unknownCol` types; downstream verbs can
    // refine types via their own `propagateSchema` if they need to.
    //
    // Side-effect: joining a `LazyDataset` is unsupported by the
    // dispatch wrapper's lazy branch (which requires a propagator);
    // chat-side workflows must `(materialize …)` before joining.
    referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
      const parsed = data as z.infer<typeof join>;
      // Only a bare-symbol OUTER side is unambiguously a column on
      // the threaded schema. `(col …)` sides are schema-dependent
      // (the column may live on the other dataset; a leading label
      // may be a binding name) and the inner side of a pair resolves
      // against the OTHER dataset — both are resolved at apply time,
      // not statically type-checked here, to avoid false positives.
      return (parsed.on as readonly unknown[]).flatMap((entry, i): readonly ColumnRefInArg[] => {
        const outer = Array.isArray(entry) ? entry[0] : entry;
        return isSym(outer) ? [{ name: outer.$sym, path: paramPath(basePath, i) } as ColumnRefInArg] : [];
      });
    },
  });

// Resolve positional[0]'s Dataset value. The arg's shape determines
// the resolution path: bare symbol → session binding lookup;
// inline Dataset literal → use directly.
//
// SpecialVerbCtx doesn't carry env, so for now we support only:
//   - direct Dataset literals
//   - bare symbols resolved via the shared `resolveBindingDataset`
//     SSOT (unbound → `UnboundSymbolError` with did-you-mean
//     suggestions, consistent with the rest of the engine)
const resolveDataset = async (arg: unknown, ctx: SpecialVerbCtx, argPath: string): Promise<Dataset> => {
  const callPos = opCallPos(ctx.spans, argPath);
  if (arg !== null && typeof arg === 'object' && '$sym' in (arg as { $sym?: unknown })) {
    const name = (arg as { $sym: string }).$sym;
    return resolveBindingDataset(ctx.evalCtx.session, name, '(join <other-Dataset> …)', callPos);
  }
  return expectDataset(arg as never, '(join <other-Dataset> …)', callPos);
};
